import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Reveal from "./Reveal";
import { Arrow } from "./Icons";
import * as Icons from "./Icons";

/* Scroll to top (or to hash) on navigation */
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

export function SectionHeading({ eyebrow, title, text, center = false, children }) {
  return (
    <Reveal className={`sec-head ${center ? "center" : ""}`}>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {children}
    </Reveal>
  );
}

export function PageHead({ crumb, title, text, children }) {
  return (
    <section className="page-head">
      <div className="container">
        <Reveal>
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span>/</span><span>{crumb}</span>
          </nav>
          <h1>{title}</h1>
          {text && <p className="lead">{text}</p>}
          {children}
        </Reveal>
      </div>
    </section>
  );
}

export function Icon({ name, ...rest }) {
  const C = Icons[name] || Icons.Spark;
  return <C {...rest} />;
}

export function Counter({ value, suffix = "", duration = 1800 }) {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started) {
        started = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setN(Math.round(value * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);
  return <strong ref={ref} className="grad-text">{n}{suffix}</strong>;
}

export function Marquee({ items }) {
  const list = [...items, ...items];
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee__track">
        {list.map((t, i) => (
          <span className="marquee__item" key={i}><i />{t}</span>
        ))}
      </div>
    </div>
  );
}

export function FAQ({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="faq">
      {items.map((f, i) => (
        <Reveal key={f.q} delay={i * 50} className={`faq__item ${open === i ? "open" : ""}`}>
          <button className="faq__q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}>
            <span>{f.q}</span><span aria-hidden="true">+</span>
          </button>
          <div className="faq__a"><p>{f.a}</p></div>
        </Reveal>
      ))}
    </div>
  );
}

export function CTABand({ title = "Ready to build the future of your business?", text = "Tell us about your idea. Within one business day you will hear back from a senior engineer, not a sales bot.", primary = "Start a project", secondary = "Explore services" }) {
  return (
    <section className="section--tight">
      <div className="container">
        <Reveal className="cta-band">
          <span className="eyebrow" style={{ justifyContent: "center" }}>Let's talk</span>
          <h2>{title}</h2>
          <p className="lead" style={{ marginInline: "auto" }}>{text}</p>
          <div className="hero__actions">
            <Link to="/contact" className="btn btn--primary btn--lg">{primary} <Arrow /></Link>
            <Link to="/services" className="btn btn--ghost btn--lg">{secondary}</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});
